import { i18n } from '@/shared/i18n';

/** Matches registry agent IDs (see API agent metadata defaults / springboard labels where applicable). */
const AUDIENCE_FLOW_AGENT_ID = 'audience-flow-agent';
const EXTERNAL_TOOLS_AGENT_ID = 'external-tools-agent';
const FLOW_DIGEST_AGENT_ID = 'flow-digest-agent';
const STRATEGY_AGENT_V3_ID = 'strategy-agent-v3';
const IDEATION_BRIEF_INTAKE_AGENT_ID = 'ideation-brief-intake';
const TRANSLATION_AGENT_ID = 'translation-agent';
const SENTINEL_BUDDY_AGENT_ID = 'sentinel-buddy-agent';

const EMAIL_FLOW_AGENT_ID = 'email-flow-agent';
// eslint-disable-next-line i18next/no-literal-string -- agent registry id
const TEMPLATES_AGENT_ID = 'templates';
const IMAGE_GENERATION_AGENT_ID = 'image-generation';
const DOUGHBOY_IMAGE_AGENT_ID = 'doughboy-image-agent';
const COPY_GENERATION_AGENT_ID = 'copy-generation';
const VIDEO_GENERATION_AGENT_ID = 'video-generation';
// eslint-disable-next-line i18next/no-literal-string -- agent registry id
const IDEATION_AGENT_ID = 'ideation';

/**
 * Registry-aligned product labels for system agents that support display overrides.
 *
 * **Tools Configuration** (admin table list + breadcrumbs on those pages): use
 * {@link getAdminToolsListDisplayName} / {@link getAdminToolsListDisplayDescription} — for mapped
 * IDs, always show the **canonical** name/description from this map (stable product label).
 * Display overrides from rename **do not** replace those labels in the admin list/breadcrumbs.
 *
 * **Basic Information** (form) and the **rest of the product** (Springboard, chat, menus) use
 * merged API data (`GET /agents`, etc.).
 */
const CANONICAL_ADMIN_TOOL_LABELS: Partial<Record<string, { name: string; description: string }>> =
  {
    [AUDIENCE_FLOW_AGENT_ID]: {
      // Getters, not values: resolving per read is what lets the label follow a language change.
      get name() {
        return i18n.t('tools:audienceFlowAgent.name');
      },
      get description() {
        return i18n.t('tools:audienceFlowAgent.description');
      },
    },
    [EXTERNAL_TOOLS_AGENT_ID]: {
      get name() {
        return i18n.t('tools:externalToolsAgent.name');
      },
      get description() {
        return i18n.t('tools:externalToolsAgent.description');
      },
    },
    [FLOW_DIGEST_AGENT_ID]: {
      get name() {
        return i18n.t('tools:flowDigestAgent.name');
      },
      get description() {
        return i18n.t('tools:flowDigestAgent.description');
      },
    },
    [STRATEGY_AGENT_V3_ID]: {
      get name() {
        return i18n.t('tools:strategyAgentV3.name');
      },
      get description() {
        return i18n.t('tools:strategyAgentV3.description');
      },
    },
    [IDEATION_BRIEF_INTAKE_AGENT_ID]: {
      get name() {
        return i18n.t('tools:ideationBriefIntakeAgent.name');
      },
      get description() {
        return i18n.t('tools:ideationBriefIntakeAgent.description');
      },
    },
    [TRANSLATION_AGENT_ID]: {
      get name() {
        return i18n.t('tools:translationAgent.name');
      },
      get description() {
        return i18n.t('tools:translationAgent.description');
      },
    },
    [SENTINEL_BUDDY_AGENT_ID]: {
      get name() {
        return i18n.t('tools:sentinelBuddyAgent.name');
      },
      get description() {
        return i18n.t('tools:sentinelBuddyAgent.description');
      },
    },
    [EMAIL_FLOW_AGENT_ID]: {
      get name() {
        return i18n.t('tools:emailFlowAgent.name');
      },
      get description() {
        return i18n.t('tools:emailFlowAgent.description');
      },
    },
    [TEMPLATES_AGENT_ID]: {
      get name() {
        return i18n.t('tools:templatesAgent.name');
      },
      get description() {
        return i18n.t('tools:templatesAgent.description');
      },
    },
    [IMAGE_GENERATION_AGENT_ID]: {
      get name() {
        return i18n.t('tools:imageGenerationAgent.name');
      },
      get description() {
        return i18n.t('tools:imageGenerationAgent.description');
      },
    },
    [DOUGHBOY_IMAGE_AGENT_ID]: {
      get name() {
        return i18n.t('tools:doughboyImageAgent.name');
      },
      get description() {
        return i18n.t('tools:doughboyImageAgent.description');
      },
    },
    [COPY_GENERATION_AGENT_ID]: {
      get name() {
        return i18n.t('tools:copyGenerationAgent.name');
      },
      get description() {
        return i18n.t('tools:copyGenerationAgent.description');
      },
    },
    [VIDEO_GENERATION_AGENT_ID]: {
      get name() {
        return i18n.t('tools:videoGenerationAgent.name');
      },
      get description() {
        return i18n.t('tools:videoGenerationAgent.description');
      },
    },
    [IDEATION_AGENT_ID]: {
      get name() {
        return i18n.t('tools:ideationAgent.name');
      },
      get description() {
        return i18n.t('tools:ideationAgent.description');
      },
    },
  };

/** Product registry name only; empty string when `agentId` is not a mapped system tool. */
export function getRegistryAdminToolName(agentId: string): string {
  return CANONICAL_ADMIN_TOOL_LABELS[agentId]?.name ?? '';
}

/** Product registry description only; empty string when `agentId` is not a mapped system tool. */
export function getRegistryAdminToolDescription(agentId: string): string {
  return CANONICAL_ADMIN_TOOL_LABELS[agentId]?.description ?? '';
}

/**
 * Name for Tools Configuration list and admin breadcrumbs on those pages: **canonical registry
 * label** for mapped system tools (ignores display overrides from the API). Other agents use the
 * API name trimmed.
 */
export function getAdminToolsListDisplayName(agentId: string, apiName: string): string {
  const fromRegistry = getRegistryAdminToolName(agentId);
  if (fromRegistry !== '') {
    return fromRegistry;
  }
  return apiName.trim();
}

/**
 * Description for Tools Configuration list: same rule as {@link getAdminToolsListDisplayName}.
 */
export function getAdminToolsListDisplayDescription(
  agentId: string,
  apiDescription: string,
): string {
  const fromRegistry = getRegistryAdminToolDescription(agentId);
  if (fromRegistry !== '') {
    return fromRegistry;
  }
  return apiDescription.trim();
}
